// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useApp } from '../../store/AppContext.js';
import { parseOfx } from '../../lib/import/ofx-parser.js';
import { detectDuplicates } from '../../lib/import/duplicate-detector.js';
import { applyPayeeSuggestions } from '../../lib/import/payee-matcher.js';
import { buildTransactionPayload, findImbalanceAccount } from '../../lib/import/pipeline.js';
import type { ReviewedTransaction, ImportResult } from '../../lib/import/types.js';
import { buildEvent, validateSplitsBalance, formatAmount } from '../../lib/eventLog.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { ImportReviewTable } from './ImportReviewTable.js';

interface Props {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
  preselectedAccountId?: string;
}

type Step = 'select' | 'match' | 'review' | 'done';

const selectCls = 'w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none';
const backBtnCls = 'px-4 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors';
const primaryBtnCls = 'px-4 py-2 bg-accent text-white text-sm hover:bg-accent-hover disabled:opacity-50 transition-colors';

export function OfxImport({ worker, keyData, preselectedAccountId }: Props) {
  const { t } = useTranslation(['import', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const navigate = useNavigate();
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const [step, setStep] = useState<Step>('select');
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<ImportResult | null>(null);
  const [accountId, setAccountId] = useState(preselectedAccountId ?? '');
  const [reviewed, setReviewed] = useState<ReviewedTransaction[]>([]);
  const [importing, setImporting] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [error, setError] = useState('');

  const accounts = [...ledger.accounts.values()].filter((a) => !a.hidden && !a.placeholder);


  const handleFile = useCallback((file: File) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const result = parseOfx(content, { accountId: '' });
      if (result.accounts[0] && !preselectedAccountId) {
        const externalId = result.accounts[0].externalId;
        const last4 = externalId.slice(-4);
        const matched = [...ledger.accounts.values()].find((a) => a.code === externalId || a.code.endsWith(last4));
        if (matched) setAccountId(matched.id);
      }
      setParsed(result);
      setStep('match');
    };
    reader.readAsText(file);
  }, [ledger, preselectedAccountId]);

  const handleFilePick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleMatch = () => {
    if (!accountId) { setError(t('selectTargetAccount')); return; }
    if (!parsed) return;
    setError('');
    const fileInput = document.getElementById('ofx-file-input') as HTMLInputElement;
    const file = fileInput?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        const result = parseOfx(content, { accountId });
        const withDup = detectDuplicates(result.transactions, ledger);
        setReviewed(applyPayeeSuggestions(withDup, ledger));
        setStep('review');
      };
      reader.readAsText(file);
    } else {
      const txns = parsed.transactions.map((t) => ({ ...t, accountId }));
      const withDup = detectDuplicates(txns, ledger);
      setReviewed(applyPayeeSuggestions(withDup, ledger));
      setStep('review');
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setError('');
    const sessionId = uuidv4();
    const imbalanceAccountId = findImbalanceAccount(ledger, 'USD') ?? accountId;
    const toImport = reviewed.filter((t) => t.selected);
    const nextSeq = ledger.lastSequence + 1;
    let count = 0;

    for (let i = 0; i < toImport.length; i++) {
      const tx = toImport[i]!;
      try {
        const payload = buildTransactionPayload(tx, sessionId, ledger, imbalanceAccountId);
        validateSplitsBalance(payload.splits);
        const event = await buildEvent('TransactionPosted', payload, nextSeq + i);
        await encryptAndPush(event);
        count++;
        setImportedCount(count);
      } catch (err) {
        setError(t('errorOnTransaction', { index: i + 1, message: err instanceof Error ? err.message : String(err) }));
        setImporting(false);
        return;
      }
    }
    setImporting(false);
    setStep('done');
  };

  if (step === 'select') {
    return (
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-border p-12 text-center hover:border-accent transition-colors"
      >
        <p className="font-mono text-2xl text-accent mb-3">OFX</p>
        <p className="text-text-muted text-sm mb-2">{t('ofx.dropZone')}</p>
        <p className="text-xs text-text-subtle mb-4">{t('ofx.duplicateNote')}</p>
        <label className={`${primaryBtnCls} cursor-pointer`}>
          {t('ofx.chooseFile')}
          <input id="ofx-file-input" type="file" accept=".ofx,.qfx" className="hidden" onChange={handleFilePick} />
        </label>
      </div>
    );
  }

  if (step === 'match' && parsed) {
    const detectedAcct = parsed.accounts[0];
    return (
      <div className="space-y-6">
        <div className="text-xs text-text-muted">{t('csv.file')} <span className="font-mono text-text">{fileName}</span></div>

        {detectedAcct && (
          <div className="p-3 bg-bg-elevated border border-border text-sm">
            <p className="text-xs text-text-muted uppercase tracking-wide mb-1">{t('ofx.detectedAccount')}</p>
            <p className="text-text font-mono">{detectedAcct.name} ({detectedAcct.type}) — {detectedAcct.currency}</p>
          </div>
        )}

        <div>
          <label className="block text-xs text-text-muted mb-1">{t('ofx.importIntoAccount')}</label>
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className={selectCls}>
            <option value="">{t('selectAccount')}</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>

        <div className="text-sm text-text-muted font-mono">
          <p>{t('transactionsFound', { count: parsed.totalTransactions })}</p>
          {parsed.dateRange.start && (
            <p className="text-xs text-text-subtle">{t('ofx.dateRange', { start: parsed.dateRange.start, end: parsed.dateRange.end })}</p>
          )}
          {parsed.closingBalance !== undefined && (
            <p className="text-xs text-text-subtle">{t('ofx.closingBalance', { balance: formatAmount(parsed.closingBalance, parsed.transactions[0]?.currency ?? 'USD') })}</p>
          )}
        </div>

        {parsed.errors.length > 0 && (
          <div className="p-3 border border-warning text-warning text-sm">
            {t('ofx.parseErrors', { count: parsed.errors.length, message: parsed.errors[0]?.message ?? '' })}
          </div>
        )}

        {error && <p className="text-negative text-sm">{error}</p>}

        <div className="flex gap-3">
          <button onClick={() => setStep('select')} className={backBtnCls}>{t('common:actions.back')}</button>
          <button onClick={handleMatch} className={primaryBtnCls}>{t('ofx.previewImport')}</button>
        </div>
      </div>
    );
  }

  if (step === 'review') {
    const toImport = reviewed.filter((t) => t.selected);
    return (
      <div className="space-y-4">
        <ImportReviewTable transactions={reviewed} accounts={accounts} onChange={setReviewed} />
        {error && <p className="text-negative text-sm">{error}</p>}
        <div className="flex gap-3">
          <button onClick={() => setStep('match')} className={backBtnCls}>{t('common:actions.back')}</button>
          <button
            onClick={() => { void handleImport(); }}
            disabled={toImport.length === 0 || importing}
            className={primaryBtnCls}
          >
            {importing ? t('importingProgress', { current: importedCount, total: toImport.length }) : t('importCount', { count: toImport.length })}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className="text-center space-y-4 py-8">
        <p className="font-mono text-positive text-2xl">✓</p>
        <p className="text-md font-medium text-text">{t('importedCount', { count: importedCount })}</p>
        <div className="flex gap-3 justify-center">
          {accountId && (
            <button onClick={() => navigate(`/accounts/${accountId}`)} className={primaryBtnCls}>
              {t('viewAccountRegister')}
            </button>
          )}
          <button onClick={() => { setStep('select'); setParsed(null); setFileName(''); }} className={backBtnCls}>
            {t('importAnotherFile')}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
